/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawerFocusFromDeepLink.ts
 * FILE_ID: UI-KANBAN-FOCUS-001
 * PURPOSE: Pure I→O helpers for mention deep-link focus inside the card drawer.
 * WHY: Notification action_urls carry ?focus=&id=; the drawer must scroll to
 *   Team Chat / Card notes / Description (and optionally a row) without
 *   coupling DOM queries to React lifecycle.
 * FLOW:
 *   ┌─────────────┐   ┌──────────────────┐   ┌─────────────────┐
 *   │ focus+id    │ → │ resolve selector │ → │ element | null  │
 *   └─────────────┘   └──────────────────┘   └─────────────────┘
 */

export type KanbanDrawerFocusSection = 'team-chat' | 'notes' | 'description'

export function isKanbanDrawerFocusSection(
  value: string | null | undefined,
): value is KanbanDrawerFocusSection {
  return value === 'team-chat' || value === 'notes' || value === 'description'
}

/** Build a stable key so focus runs once per distinct deep-link tuple. */
export function drawerFocusKey(
  cardId: string | null | undefined,
  focus: string | null | undefined,
  entityId: string | null | undefined,
): string | null {
  if (!cardId || !focus || !isKanbanDrawerFocusSection(focus)) return null
  return `${cardId}|${focus}|${entityId ?? ''}`
}

/**
 * Prefer a specific message/comment row when ``entityId`` is set; otherwise
 * the section root. Returns null when nothing is in the DOM yet.
 */
/** Escape a value for use inside a double-quoted CSS attribute selector. */
function escapeAttr(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

export function queryDrawerFocusTarget(
  root: ParentNode | Document,
  focus: KanbanDrawerFocusSection,
  entityId?: string | null,
): Element | null {
  if (entityId) {
    if (focus === 'team-chat') {
      const row = root.querySelector(
        `[data-kanban-message-id="${escapeAttr(entityId)}"]`,
      )
      if (row) return row
    }
    if (focus === 'notes') {
      const row = root.querySelector(
        `[data-kanban-comment-id="${escapeAttr(entityId)}"]`,
      )
      if (row) return row
    }
  }
  return root.querySelector(`[data-kanban-focus="${focus}"]`)
}

const HIGHLIGHT_CLASS =
  'ring-2 ring-sky-400 bg-sky-50 dark:bg-sky-950/40 dark:ring-sky-500'

/**
 * Scroll + brief highlight. Fail-soft: no-ops when el is null / scroll missing.
 */
export function applyDrawerFocus(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  } catch {
    // jsdom / older engines — ignore
  }
  const classes = HIGHLIGHT_CLASS.split(/\s+/).filter(Boolean)
  el.classList.add(...classes)
  window.setTimeout(() => {
    el.classList.remove(...classes)
  }, 1600)
  return true
}
